import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from "@angular/core";
import Hls from "hls.js";
import { ContentControlLiveSource } from 'app/js/src/domain/content-control/live-source';
import { MillisecondsToFormattedDuration } from "app/js/src/domain/common/ui-utils";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";

interface AudioTrack {
  id: number;
  groupId: string;
  language: string;
  name: string;
}

interface SubtitleTrack {
  id: number;
  groupId: string;
  language: string;
  name: string;
}

interface ProgressTick {
  x: number;
  y1: number;
  y2: number;
}

@Component({
  selector: "evrideo-content-control-operation-room-player",
  templateUrl: "./content-control-operation-room-player.component.html",
  styleUrl: "./content-control-operation-room-player.component.scss",
  animations: [
    trigger("tickAnimation", [
      state(
        "visible",
        style({
          opacity: 0.85,
        }),
      ),
      state(
        "fadeOut",
        style({
          opacity: 0,
        }),
      ),
      transition("visible => fadeOut", animate("300ms ease-out")),
      transition("void => visible", animate("300ms ease-in")),
    ]),
  ],
})
export class ContentControlOperationRoomPlayerComponent
  implements OnInit, OnDestroy
{
  @Input() liveSource: ContentControlLiveSource;
  @ViewChild("fullscreenContainer") fullscreenContainer!: ElementRef;
  @ViewChild("videoPlayer") videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild("videoCanvas") videoCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild("playhead") playhead!: ElementRef<HTMLDivElement>;
  @ViewChild("progressSvg") progressSvg?: ElementRef<SVGElement>;

  MillisecondsToFormattedDuration = MillisecondsToFormattedDuration;
  private hls: Hls | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private audioSource: MediaElementAudioSourceNode | null = null;
  private animationFrameId: number | null = null;
  private gainNode: GainNode | null = null;
  private liveEdge = 0;
  private backlogStart = 0;

  private readonly LIVE_THRESHOLD = 1; // Seconds behind live to still consider "live"
  private readonly TOTAL_BARS = 21;
  private readonly BAR_WIDTH = 15;
  private readonly BAR_HEIGHT = 2;
  private readonly BAR_GAP = 1.0;
  private readonly BAR_COLOR = "#56CA00";
  private SEGMENT_DURATION = 5; // seconds
  private SEGMENTS_COUNT = 120;
  private BACKLOG_DURATION = this.SEGMENT_DURATION * this.SEGMENTS_COUNT; // 10 minutes

  progressBars: { x: number; tall: boolean }[] = [];
  currentTime: number = 0;
  duration: number = 900; // 15 minutes in seconds
  isLive = true;
  isPlaying = false;
  onDrag = false;
  audioTracks: AudioTrack[] = [];
  subtitleTracks: SubtitleTrack[] = [];
  currentAudioTrack: number = -1;
  currentSubtitleTrack: number = -1;
  isMuted: boolean = true;

  private fixedPosition: number | null = null;
  private readonly TICK_SPACING = 10; // Adjust as needed
  private readonly PROGRESS_BAR_TICKS = 180; // Fixed number of visual ticks
  progressTicks: {
    x: number;
    y1: number;
    y2: number;
  }[] = [];

  tickToSegmentRatio = 1; // Will be calculated based on SEGMENTS_COUNT

  tooltipPosition: number | null = null;
  tooltipTime: number = 0;

  isDragging = false;
  isFullScreen: boolean;
  updateInterval: number;
  lastUpdateTime: number;
  progressBarAnimationFrameId: number;
  tickDistance: number;
  lastTimeUpdate: number;

  private boundPlay: () => void;
  private boundPause: () => void;
  private boundTimeUpdate: () => void;
  private boundPreventContext: (event: Event) => void;

  constructor() {
    this.progressTicks = Array(this.PROGRESS_BAR_TICKS)
      .fill(0)
      .map((_, index) => ({
        x: index + 1,
        y1: index % 5 === 0 ? 2.16064 : 2.97021,
        y2: index % 5 === 0 ? 3.7794 : 3.77959,
      }));
  }
  ngOnInit() {
    if (!Hls.isSupported()) {
      console.error("HLS is not supported in this browser.");
    }
  }
  ngOnDestroy() {
    this.cleanResources();
    document.removeEventListener("mousemove", this.onDocumentMouseMove);
    document.removeEventListener("mouseup", this.onDocumentMouseUp);
  }

  onDrop(event) {
    this.onDrag = false;
    if (this.liveSource) {
      return;
    }
    const data = event.dataTransfer.getData(
      "text/content-control-live-source-item",
    );
    if (data) {
      const liveSource: ContentControlLiveSource = JSON.parse(data);
      if (liveSource) {
        this.liveSource = liveSource;
        setTimeout(() => {
          this.initializeHlsPlayer();
          this.startProgressAnimation();
          this.videoPlayer.nativeElement.addEventListener(
            "contextmenu",
            (event) => {
              event.preventDefault();
            },
          );
        }, 0);
      }
    }
  }
  onDragEnter() {
    this.onDrag = true;
  }
  onDragLeave() {
    this.onDrag = false;
  }

  changeAudioTrack(trackId: number) {
    if (this.hls) {
      this.hls.audioTrack = trackId;
      this.currentAudioTrack = trackId;
    }
  }

  changeSubtitleTrack(trackId: number) {
    if (this.hls) {
      const video = this.videoPlayer.nativeElement;

      // First, remove any existing text tracks
      for (let i = video.textTracks.length - 1; i >= 0; i--) {
        video.textTracks[i].mode = "disabled";
      }
      if (trackId === -1) {
        // Disable subtitles
        this.hls.subtitleDisplay = false;
        this.hls.subtitleTrack = -1;
      } else {
        // Enable subtitles
        this.hls.subtitleDisplay = true;
        this.hls.subtitleTrack = trackId;
        // Make sure the text track is showing
        if (video.textTracks[trackId]) {
          video.textTracks[trackId].mode = "showing";
        }
      }

      this.currentSubtitleTrack = trackId;
    }
  }
  closeVideo(event: Event) {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      this.isFullScreen = false;
    } else {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.cleanResources();
      this.liveSource = null;
    }
  }
  cleanResources() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.progressBarAnimationFrameId !== null) {
      cancelAnimationFrame(this.progressBarAnimationFrameId);
    }

    // Remove event listeners before destroying HLS
    if (this.videoPlayer?.nativeElement) {
      this.videoPlayer.nativeElement.removeEventListener(
        "play",
        this.boundPlay,
      );
      this.videoPlayer.nativeElement.removeEventListener(
        "pause",
        this.boundPause,
      );
      this.videoPlayer.nativeElement.removeEventListener(
        "timeupdate",
        this.boundTimeUpdate,
      );
      this.videoPlayer.nativeElement.removeEventListener(
        "contextmenu",
        this.boundPreventContext,
      );
    }

    this.destroyHls();
    this.cleanupAudio();
  }
  toggleMute() {
    if (this.videoPlayer) {
      this.isMuted = !this.isMuted;
      this.gainNode.gain.value = this.isMuted ? 0 : 1;
    }
  }

  moveToLiveEdge() {
    if (this.hls?.liveSyncPosition) {
      const targetTime = this.hls.liveSyncPosition - this.LIVE_THRESHOLD / 2;
      this.currentTime = targetTime;
      if (this.hls.media) {
        this.hls.media.currentTime = targetTime;
      }
      this.isLive = true;
      this.fixedPosition = null;
    }
  }

  getPlayheadPosition(): number {
    if (!this.hls?.liveSyncPosition) return 0;

    if (!this.isLive && this.fixedPosition !== null) {
      return this.fixedPosition;
    }
    const currentSegment = Math.floor(this.duration / this.SEGMENT_DURATION);
    return currentSegment / this.tickToSegmentRatio;
  }
  togglePlayPause() {
    if (this.hls?.media) {
      if (this.isPlaying) {
        this.hls.media.pause();
      } else {
        this.hls.media.play().catch((error) => {
          console.warn("Playback failed:", error);
        });
      }
    }
  }
  startProgressAnimation() {
    setInterval(() => {
      if (!this.isDragging) {
        this.updateProgressBar();
      }
    }, 5000); // Update every 5 seconds
  }

  updateProgressBar() {
    // Logic to update the progress bar, e.g., adding new lines
    // This can be done by updating the SVG dynamically if needed
  }

  toggleFullscreen() {
    const elem = this.fullscreenContainer.nativeElement;
    if (!document.fullscreenElement) {
      elem
        .requestFullscreen()
        .then(() => {
          this.isFullScreen = true;
          const fullscreenDispose = () => {
            if (!document.fullscreenElement) {
              this.isFullScreen = false;
              document.removeEventListener(
                "fullscreenchange",
                fullscreenDispose,
              );
            }
          };
          document.addEventListener("fullscreenchange", fullscreenDispose);
        })
        .catch((err: any) => {
          this.isFullScreen = false;
          console.error(
            `Error attempting to enable full-screen mode: ${err.message} (${err.name})`,
          );
        });
    } else {
      this.isFullScreen = false;
      document.exitFullscreen();
    }
  }

  onProgressBarMouseLeave() {
    this.tooltipPosition = null;
  }
  onProgressBarClick(event: MouseEvent) {
    const rect = (event.target as SVGElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = x / rect.width;

    const seekTime = percentage * this.duration;
    if (this.videoPlayer?.nativeElement) {
      this.videoPlayer.nativeElement.currentTime = seekTime;
    }
  }

  onProgressBarMouseMove(event: MouseEvent) {
    let rect: DOMRect;
    if (event.target instanceof SVGRectElement) {
      rect = event.target.getBoundingClientRect();
    } else if (event.target instanceof SVGLineElement) {
      rect = event.target.parentElement
        .querySelector("rect")
        .getBoundingClientRect();
    } else {
      return;
    }
    const x = event.clientX - rect.left;
    const percentage = x / rect.width;

    this.tooltipTime = percentage * this.duration;
    this.tooltipPosition = x;

    if (this.isDragging) {
      this.seek(percentage * this.duration);
    }
  }

  onProgressBarMouseDown(event: MouseEvent) {
    this.isDragging = true;
    document.body.style.userSelect = "none";

    // Initial seek
    const rect = (event.target as SVGElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = 1 - x / rect.width;
    this.seek(percentage * this.duration);

    document.addEventListener("mousemove", this.onDocumentMouseMove);
    document.addEventListener("mouseup", this.onDocumentMouseUp);
  }

  private initializeHlsPlayer() {
    this.hls = new Hls({
      debug: false,
      enableWorker: true,
      lowLatencyMode: false,
      startPosition: -1,
      maxBufferLength: 30,
      maxMaxBufferLength: 30,
      backBufferLength: 30,
      liveSyncDurationCount: 1,
      liveMaxLatencyDurationCount: 6,
      renderTextTracksNatively: true,
    });

    this.hls.loadSource(this.liveSource.url);
    this.hls.attachMedia(this.videoPlayer.nativeElement);

    this.videoPlayer.nativeElement.muted = false;

    this.boundPlay = this.play.bind(this);
    this.boundPause = this.pause.bind(this);
    this.boundTimeUpdate = this.timeUpdate.bind(this);
    this.boundPreventContext = (event) => event.preventDefault();

    this.videoPlayer.nativeElement.addEventListener("play", this.boundPlay);
    this.videoPlayer.nativeElement.addEventListener("pause", this.boundPause);
    this.videoPlayer.nativeElement.addEventListener(
      "timeupdate",
      this.boundTimeUpdate,
    );
    this.videoPlayer.nativeElement.addEventListener(
      "contextmenu",
      this.boundPreventContext,
    );

    // Handle HLS events
    this.hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, (event, data) => {
      this.loadAudioTracks();
    });

    this.hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (event, data) => {
      this.loadSubtitleTracks();
    });
    this.hls.on(Hls.Events.MANIFEST_PARSED, async () => {
      // Method 1: Check levels
      const levels = this.hls.levels;
      if (levels.length > 0) {
        console.log("FragmentsLength:", levels[0].details?.fragments.length);
      }

      // Method 2: Check live details
      if (this.hls.liveSyncPosition) {
        console.log("Live Sync Position:", this.hls.liveSyncPosition);
      }
      // Also add these new event handlers

      this.moveToLiveEdge();
      await this.videoPlayer.nativeElement.play();
      this.isPlaying = true;
      this.initializeAudioAnalyzer();

      if (this.hls.levels.length > 0 && this.hls.levels[0].details) {
        const levelDetails = this.hls.levels[0].details;

        // Update component properties
        this.duration = levelDetails.totalduration;
        this.SEGMENT_DURATION = levelDetails.targetduration;
        this.SEGMENTS_COUNT = levelDetails.fragments.length;
        this.BACKLOG_DURATION = this.SEGMENT_DURATION * this.SEGMENTS_COUNT;

        // Update HLS configuration
        const hlsConfig = {
          maxBufferLength: this.duration,
          maxMaxBufferLength: this.duration,
          backBufferLength: this.duration,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: Math.max(6, this.SEGMENTS_COUNT),
        };

        Object.assign(this.hls.config, hlsConfig);
      }

      this.calculateProgressTicks();
    });

    this.hls.on(Hls.Events.LEVEL_UPDATED, (_, data) => {
      if (data.details) {
        this.liveEdge = data.details.live ? data.details.edge : 0;
        this.backlogStart = this.liveEdge - this.BACKLOG_DURATION;

        if (!this.isDragging) {
          this.currentTime = this.hls.media?.currentTime || 0;

          if (this.isLive) {
            this.fixedPosition = null;
            this.updateLiveStatus();
          }
        }
      }
    });

    this.videoPlayer.nativeElement.addEventListener(
      "timeupdate",
      this.timeUpdate.bind(this),
    );

    this.hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            this.hls?.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            this.hls?.recoverMediaError();
            break;
          default:
            this.destroyHls();
            break;
        }
      }
    });
  }
  private initializeAudioAnalyzer() {
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.gainNode = this.audioContext.createGain();
    this.audioSource = this.audioContext.createMediaElementSource(
      this.videoPlayer.nativeElement,
    );

    // Configure analyzer
    this.analyser.fftSize = 32;
    this.analyser.smoothingTimeConstant = 0.8;

    // Connect the audio graph:
    // Source -> Analyser -> GainNode -> Destination
    this.audioSource.connect(this.analyser);
    this.analyser.connect(this.gainNode);
    this.gainNode.connect(this.audioContext.destination);

    // Set initial gain to 0 (muted) but analyzer still receives signal
    this.gainNode.gain.value = 0;

    this.startVisualization();
  }
  private updateLiveStatus() {
    if (!this.hls?.liveSyncPosition) return;

    const behindLive = this.hls.liveSyncPosition - this.currentTime;
    this.isLive = behindLive <= this.LIVE_THRESHOLD;
  }
  private startVisualization() {
    const canvas = this.videoCanvas.nativeElement;
    const ctx = canvas.getContext("2d")!;
    const analyser = this.analyser!;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const draw = () => {
      this.animationFrameId = requestAnimationFrame(draw);

      // Get canvas dimensions
      const videoWidth = this.videoPlayer.nativeElement.clientWidth;
      const videoHeight = this.videoPlayer.nativeElement.clientHeight;

      // Update canvas size
      canvas.width = videoWidth;
      canvas.height = videoHeight;

      // Get audio data
      analyser.getByteFrequencyData(dataArray);

      // Calculate the average volume
      const average =
        dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
      const volume = average / 255; // Normalize to 0-1

      // Calculate scaling factors
      const baseScale = Math.min(videoWidth, videoHeight) / 213; // Based on SVG viewBox
      const scaledBarWidth = this.BAR_WIDTH * baseScale;
      const scaledBarHeight = this.BAR_HEIGHT * baseScale;
      const scaledGap = this.BAR_GAP * baseScale;

      // Position the meter in the right corner
      const startX = 20; // 20px margin from right
      const startY = videoHeight - 20; // 20px margin from bottom

      // Clear previous frame
      ctx.clearRect(0, 0, videoWidth, videoHeight);

      // Draw only active bars
      ctx.fillStyle = this.BAR_COLOR;
      const activeBars = Math.ceil(volume * this.TOTAL_BARS);

      for (let i = 0; i < this.TOTAL_BARS; i++) {
        const y = startY - i * (scaledBarHeight + scaledGap);

        // Only draw active bars, skip inactive ones completely
        if (i < activeBars) {
          ctx.fillRect(startX, y, scaledBarWidth, scaledBarHeight);
        }
        // No else clause - we don't draw anything for inactive bars
      }
    };

    draw();
  }

  private loadAudioTracks() {
    if (this.hls) {
      const audioTracks = this.hls.audioTracks;
      this.audioTracks = audioTracks.map((track) => ({
        id: track.id,
        groupId: track.groupId,
        language: track.lang || "unknown",
        name: track.name,
      }));
    }
  }

  private loadSubtitleTracks() {
    if (this.hls) {
      const subtitleTracks = this.hls.subtitleTracks;
      this.subtitleTracks = subtitleTracks.map((track) => ({
        id: track.id,
        groupId: track.groupId,
        language: track.lang || "unknown",
        name: track.name,
      }));
    }
  }
  private stopVisualization() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private cleanupAudio() {
    this.stopVisualization();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.analyser = null;
    this.audioSource = null;
  }
  private destroyHls() {
    if (this.hls) {
      if (this.hls.media) {
        this.hls.media.pause();
      }
      this.hls.destroy();
      this.hls = null;
    }
  }

  private calculateProgressTicks() {
    this.tickToSegmentRatio = this.SEGMENTS_COUNT / this.PROGRESS_BAR_TICKS;
    if (this.duration) {
      this.updateInterval = 16;
      this.tickDistance =
        this.updateInterval / this.duration / this.TICK_SPACING;

      this.lastUpdateTime = performance.now();
      this.startAnimation();
    }
  }

  private onDocumentMouseMove = (event: MouseEvent) => {
    if (this.isDragging) {
      const svgElement = this.progressSvg?.nativeElement;
      if (svgElement) {
        const rect = svgElement.getBoundingClientRect();
        const x =
          Math.max(rect.left, Math.min(event.clientX, rect.right)) - rect.left;
        const percentage = x / rect.width;

        this.tooltipTime = Math.round(percentage * this.duration);
        this.tooltipPosition = Math.max(
          rect.left,
          Math.min(event.clientX, rect.right),
        );

        this.seek(percentage * this.duration);
      }
    }
  };

  private onDocumentMouseUp = () => {
    this.isDragging = false;
    document.body.style.userSelect = "";

    // Remove global listeners
    document.removeEventListener("mousemove", this.onDocumentMouseMove);
    document.removeEventListener("mouseup", this.onDocumentMouseUp);
    const boundedSeekTime = Math.min(
      Math.max(this.currentTime, this.backlogStart),
      this.liveEdge,
    );
    const behindLive = this.hls.liveSyncPosition - boundedSeekTime;
    this.isLive = behindLive <= this.LIVE_THRESHOLD;

    if (!this.isLive) {
      this.fixedPosition =
        Math.floor(
          this.videoPlayer.nativeElement.currentTime / this.SEGMENT_DURATION,
        ) * this.tickToSegmentRatio;
    } else {
      this.fixedPosition = null;
    }
  };

  private seek(time: number) {
    if (this.videoPlayer?.nativeElement) {
      this.videoPlayer.nativeElement.currentTime = Math.min(
        Math.max(time, 0),
        this.duration,
      );
    }
  }
  private play() {
    this.isPlaying = true;
  }
  private pause() {
    this.isPlaying = false;
  }
  private timeUpdate() {
    const now = Date.now();
    if (now - this.lastTimeUpdate < 250) return;
    this.lastTimeUpdate = now;

    if (!this.isDragging) {
      this.currentTime = this.videoPlayer.nativeElement.currentTime;

      if (this.isLive) {
        this.fixedPosition = null;
        this.updateLiveStatus();
      }
    }
  }
  private startAnimation() {
    const animate = (currentTime: number) => {
      if (currentTime - this.lastUpdateTime >= this.updateInterval) {
        if (this.progressTicks[0].x - this.tickDistance <= 0) {
          const firstTick = this.progressTicks.shift();
          this.progressTicks.forEach((tick) => {
            tick.x = Math.floor(tick.x);
          });
          firstTick.x = this.PROGRESS_BAR_TICKS;
          this.progressTicks.push(firstTick);
        } else {
          this.progressTicks.forEach((tick) => {
            tick.x -= this.tickDistance;
          });
        }
        this.lastUpdateTime = currentTime;
      }
      this.progressBarAnimationFrameId = requestAnimationFrame(animate);
    };

    this.progressBarAnimationFrameId = requestAnimationFrame(animate);
  }
}
